import React, { useState, useEffect, useCallback } from "react";
import {
  Phone, Plus, X, Tag, ClockCounterClockwise,
  Star, CalendarCheck, CurrencyDollar, ArrowsClockwise,
  CheckCircle, XCircle, ArrowDown, ArrowUp,
  ChartLineUp, Trash, NotePencil, Envelope, Buildings, MapPin, MagnifyingGlass
} from "@phosphor-icons/react";
import API from "../services/api";
import MiniDialer from "../components/MiniDialer";
import "./Leads.css";

const STATUSES = [
  { value: "new",         label: "New",         color: "#6b7280" },
  { value: "contacted",   label: "Contacted",   color: "#3b82f6" },
  { value: "qualified",   label: "Qualified",   color: "#8b5cf6" },
  { value: "proposal",    label: "Proposal",    color: "#f59e0b" },
  { value: "negotiation", label: "Negotiation", color: "#f97316" },
  { value: "won",         label: "Won",         color: "#10b981" },
  { value: "lost",        label: "Lost",        color: "#ef4444" },
];

const TAGS = ["Hot Lead","Follow Up","Not Interested","Decision Maker","Budget Confirmed","Demo Requested","Referred","Cold"];
const LEAD_SOURCES = ["Web","Phone","Email","Referral","LinkedIn","Cold Call","Event","Partner","Other"];
const NOTE_TYPES = [
  { value: "note",     label: "Note" },
  { value: "call",     label: "Call" },
  { value: "email",    label: "Email" },
  { value: "meeting",  label: "Meeting" },
  { value: "followup", label: "Follow Up" },
];

const StatusBadge = ({ status }) => {
  const s = STATUSES.find(s => s.value === status) || STATUSES[0];
  return (
    <span style={{ display:"inline-flex", alignItems:"center", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:600, background:s.color+"22", color:s.color, border:`1px solid ${s.color}44` }}>
      {s.label}
    </span>
  );
};

const ScoreStars = ({ score = 0, onChange }) => (
  <div style={{ display:"flex", gap:2 }}>
    {[1,2,3,4,5].map(i => (
      <Star key={i} size={15} weight={i <= score ? "fill" : "regular"}
        color={i <= score ? "#f59e0b" : "#374151"}
        style={{ cursor: onChange ? "pointer" : "default" }}
        onClick={() => onChange?.(i === score ? 0 : i)} />
    ))}
  </div>
);

const formatDate = (d) => { if (!d) return null; return new Date(d).toLocaleDateString([], { month:"short", day:"numeric", year:"numeric" }); };
const formatTime = (d) => {
  if (!d) return "";
  const diff = new Date() - new Date(d);
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
  return new Date(d).toLocaleDateString([], { month:"short", day:"numeric" });
};

export default function Leads() {
  const dialerState = window.__ds || {};
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailTab, setDetailTab] = useState("overview");
  const [showAdd, setShowAdd] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [callerId, setCallerId] = useState("");
  const [notes, setNotes] = useState([]);
  const [calls, setCalls] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [noteType, setNoteType] = useState("note");
  const [savingNote, setSavingNote] = useState(false);
  const [detailWidth, setDetailWidth] = useState(420);
  const [form, setForm] = useState({ first_name:"", last_name:"", phone:"", mobile:"", email:"", company:"", position:"", lead_source:"" });

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await API.get(`/contacts?type=lead&search=${search}`); setLeads(data); } catch {}
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { API.get("/twilio/credentials").then(({ data }) => setCallerId(data.caller_id || "")).catch(() => {}); }, []);

  const selectLead = async (lead) => {
    setSelected(lead); setDetailTab("overview");
    try {
      const [nr, cr] = await Promise.all([API.get(`/contacts/${lead.id}/notes`), API.get(`/contacts/${lead.id}/calls`)]);
      setNotes(nr.data); setCalls(cr.data);
    } catch {}
  };

  const updateLead = async (id, fields) => {
    try {
      const { data } = await API.patch(`/contacts/${id}`, fields);
      setLeads(ls => ls.map(l => l.id === id ? { ...l, ...data } : l));
      if (selected?.id === id) setSelected(s => ({ ...s, ...data }));
    } catch (err) { alert(err.response?.data?.error || "Update failed"); }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selected) return;
    setSavingNote(true);
    try {
      const { data } = await API.post(`/contacts/${selected.id}/notes`, { note: newNote, note_type: noteType });
      setNotes(n => [data, ...n]); setNewNote("");
      setSelected(s => ({ ...s, last_contacted: new Date().toISOString() }));
    } catch {}
    setSavingNote(false);
  };

  const deleteNote = async (noteId) => {
    try { await API.delete(`/contacts/${selected.id}/notes/${noteId}`); setNotes(n => n.filter(note => note.id !== noteId)); } catch {}
  };

  const handleSync = async () => {
    setSyncing(true); setSyncMsg("");
    try { const { data } = await API.post("/contacts/sync"); setSyncMsg(`Synced ${data.synced} records`); load(); }
    catch (err) { setSyncMsg(err.response?.data?.error || "Sync failed"); }
    setSyncing(false); setTimeout(() => setSyncMsg(""), 4000);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try { await API.post("/contacts", { ...form, is_lead: true }); setShowAdd(false); setForm({ first_name:"", last_name:"", phone:"", mobile:"", email:"", company:"", position:"", lead_source:"" }); load(); }
    catch (err) { alert(err.response?.data?.error || "Failed"); }
  };

  const startDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX, startW = detailWidth;
    const onMove = (e) => setDetailWidth(Math.max(320, Math.min(700, startW + (startX - e.clientX))));
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
  };

  const getInitials = (l) => `${l.first_name?.[0]||""}${l.last_name?.[0]||""}`.toUpperCase() || "?";
  const getPhone = (l) => l.phone || l.mobile || null;
  const filteredLeads = leads.filter(l => statusFilter === "all" || (l.status || "new") === statusFilter);
  const statusCounts = STATUSES.reduce((acc, s) => { acc[s.value] = leads.filter(l => (l.status||"new") === s.value).length; return acc; }, {});

  return (
    <div style={{display:"flex",height:"100%",overflow:"hidden"}}>
      <MiniDialer dialerState={dialerState} />
      <div className="leads-page" style={{flex:1,overflow:"hidden"}}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="page-subtitle">{leads.length} total · {leads.filter(l=>(l.status||"new")==="won").length} won</p>
        </div>
        <div className="header-actions">
          {syncMsg && <span className="sync-msg">{syncMsg}</span>}
          <button className="btn btn-secondary btn-sm" onClick={handleSync} disabled={syncing}><ArrowsClockwise size={14}/> Sync Zoho</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><Plus size={14} weight="bold"/> Add Lead</button>
        </div>
      </div>

      <div className="leads-toolbar">
        <div className="leads-search-wrap">
          <MagnifyingGlass size={16} style={{color:"var(--color-gray)",flexShrink:0}}/>
          <input className="leads-search-input" placeholder="Search leads..." value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <div className="leads-filters">
        <button className={`pipeline-stage ${statusFilter==="all"?"active":""}`} onClick={() => setStatusFilter("all")}>
          <span className="stage-count">{leads.length}</span><span className="stage-label">All</span>
        </button>
        {STATUSES.map(s => (
          <button key={s.value} className={`pipeline-stage ${statusFilter===s.value?"active":""}`}
            onClick={() => setStatusFilter(s.value)} style={{"--stage-color": s.color}}>
            <span className="stage-count" style={{color:s.color}}>{statusCounts[s.value]||0}</span>
            <span className="stage-label">{s.label}</span>
          </button>
        ))}
        </div>
      </div>
      <div className="toolbar-divider"/>

      <div className="contacts-layout" style={{position:"relative"}}>
        <div className="contacts-list">
          {loading && <div className="spinner-container"><span className="spinner"/></div>}
          {!loading && filteredLeads.length === 0 && (
            <div className="empty-state"><ChartLineUp size={32} style={{opacity:0.3}}/><p>No leads found.</p></div>
          )}
          {filteredLeads.map(lead => (
            <div key={lead.id} className={`contact-item ${selected?.id===lead.id?"active":""}`} onClick={() => selectLead(lead)}>
              <div className="contact-avatar" style={{background:(STATUSES.find(s=>s.value===(lead.status||"new"))?.color||"#6b7280")+"33"}}>
                <span style={{color:STATUSES.find(s=>s.value===(lead.status||"new"))?.color||"#6b7280"}}>{getInitials(lead)}</span>
              </div>
              <div className="contact-info">
                <div className="contact-name" style={{display:"flex",alignItems:"center",gap:6}}>
                  {lead.first_name} {lead.last_name}
                  {lead.score > 0 && <ScoreStars score={lead.score}/>}
                </div>
                <div className="contact-meta">{lead.company || getPhone(lead) || "—"}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                {lead.tags?.length > 0 && (
                  <span style={{fontSize:10,color:"var(--color-purple)",background:"rgba(178,130,240,0.15)",padding:"2px 7px",borderRadius:10,border:"1px solid rgba(178,130,240,0.3)",whiteSpace:"nowrap"}}>
                    {lead.tags[0]}{lead.tags.length > 1 ? ` +${lead.tags.length-1}` : ""}
                  </span>
                )}
                {lead.follow_up_date && new Date(lead.follow_up_date) <= new Date(Date.now()+86400000*2) && (
                  <span style={{fontSize:10,color:"#f59e0b",display:"flex",alignItems:"center",gap:3,whiteSpace:"nowrap"}}>
                    <CalendarCheck size={10}/> {formatDate(lead.follow_up_date)}
                  </span>
                )}
                <StatusBadge status={lead.status||"new"}/>
              </div>
            </div>
          ))}
        </div>

        {selected && (
          <>
            <div onMouseDown={startDrag} style={{width:4,cursor:"col-resize",background:"var(--color-surface)",flexShrink:0}}
              onMouseEnter={e=>e.target.style.background="var(--color-teal)"}
              onMouseLeave={e=>e.target.style.background="var(--color-surface)"}/>
            <div className="lead-detail" style={{width:detailWidth,minWidth:detailWidth}}>
              <div className="lead-detail-header">
                <div className="detail-avatar" style={{background:(STATUSES.find(s=>s.value===(selected.status||"new"))?.color||"#6b7280")+"33",width:44,height:44,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,flexShrink:0}}>
                  <span style={{color:STATUSES.find(s=>s.value===(selected.status||"new"))?.color||"#6b7280"}}>{getInitials(selected)}</span>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <h2 style={{margin:0,fontSize:16,fontWeight:600}}>{selected.first_name} {selected.last_name}</h2>
                    <StatusBadge status={selected.status||"new"}/>
                  </div>
                  <div style={{fontSize:12,color:"var(--color-gray)",marginTop:2}}>{selected.position}{selected.position&&selected.company?" · ":""}{selected.company}</div>
                  <ScoreStars score={selected.score||0} onChange={(score)=>updateLead(selected.id,{score})}/>
                </div>
                <button className="btn-icon" onClick={()=>setSelected(null)}><X size={16}/></button>
              </div>

              <div className="lead-quick-actions">
                {getPhone(selected) && (
                  <button className="btn btn-primary btn-sm" onClick={()=>dialerState.dial?.(getPhone(selected),callerId)}>
                    <Phone size={13} weight="fill"/> Call
                  </button>
                )}
                <select className="status-select" value={selected.status||"new"} onChange={e=>updateLead(selected.id,{status:e.target.value})}>
                  {STATUSES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <button className="btn btn-sm" style={{background:"#10b98122",color:"#10b981",border:"1px solid #10b98144"}} onClick={()=>updateLead(selected.id,{status:"won"})}>
                  <CheckCircle size={13}/> Won
                </button>
                <button className="btn btn-sm" style={{background:"#ef444422",color:"#ef4444",border:"1px solid #ef444444"}} onClick={()=>updateLead(selected.id,{status:"lost"})}>
                  <XCircle size={13}/> Lost
                </button>
              </div>

              <div className="detail-tabs">
                {["overview","notes","calls","edit"].map(t=>(
                  <button key={t} className={`detail-tab-btn ${detailTab===t?"active":""}`} onClick={()=>setDetailTab(t)}>
                    {t.charAt(0).toUpperCase()+t.slice(1)}
                    {t==="notes"&&notes.length>0&&<span className="tab-badge">{notes.length}</span>}
                    {t==="calls"&&calls.length>0&&<span className="tab-badge">{calls.length}</span>}
                  </button>
                ))}
              </div>

              <div className="lead-detail-body">
                {detailTab==="overview" && (
                  <div>
                    <div className="detail-section">
                      <span className="label">Contact Info</span>
                      {selected.phone&&<div className="detail-row"><Phone size={13}/><span>{selected.phone}</span></div>}
                      {selected.mobile&&<div className="detail-row"><Phone size={13}/><span>{selected.mobile} (mobile)</span></div>}
                      {selected.email&&<div className="detail-row"><Envelope size={13}/><span>{selected.email}</span></div>}
                      {selected.company&&<div className="detail-row"><Buildings size={13}/><span>{selected.company}</span></div>}
                      {selected.lead_source&&<div className="detail-row"><MapPin size={13}/><span>{selected.lead_source}</span></div>}
                    </div>
                    <div className="detail-section">
                      <span className="label">Sales Info</span>
                      <div className="detail-row">
                        <CurrencyDollar size={13}/><span>Lead Value</span>
                        <input type="number" className="inline-input" defaultValue={selected.lead_value||""} placeholder="0"
                          onBlur={e=>updateLead(selected.id,{lead_value:parseFloat(e.target.value)||0})}/>
                      </div>
                      <div className="detail-row">
                        <CalendarCheck size={13}/><span>Follow Up</span>
                        <input type="date" className="inline-input"
                          key={selected.follow_up_date||"no-date"}
                          defaultValue={selected.follow_up_date?selected.follow_up_date.split("T")[0]:""}
                          onChange={e=>updateLead(selected.id,{follow_up_date:e.target.value||null})}
                          style={{colorScheme:"dark"}}/>
                      {selected.follow_up_date && (
                        <button onClick={()=>updateLead(selected.id,{follow_up_date:null})}
                          style={{background:"none",border:"none",color:"var(--color-danger)",cursor:"pointer",padding:"0 4px",display:"flex",alignItems:"center",gap:3,fontSize:11,whiteSpace:"nowrap",flexShrink:0}}>
                          <Trash size={12}/> Clear
                        </button>
                      )}
                      </div>
                      {selected.last_contacted&&<div className="detail-row"><ClockCounterClockwise size={13}/><span>Last Contact</span><span style={{color:"var(--color-gray)",fontSize:12}}>{formatDate(selected.last_contacted)}</span></div>}
                    </div>
                    <div className="detail-section">
                      <span className="label">Tags</span>
                      <div className="tags-grid">
                        {TAGS.map(tag=>(
                          <button key={tag} className={`tag-btn ${(selected.tags||[]).includes(tag)?"active":""}`}
                            onClick={()=>{const c=selected.tags||[];updateLead(selected.id,{tags:c.includes(tag)?c.filter(t=>t!==tag):[...c,tag]});}}>
                            {tag}
                          </button>
                        ))}
                      </div>
                    </div>
                    {notes.length>0&&(
                      <div className="detail-section">
                        <span className="label">Latest Note</span>
                        <div className="note-preview">
                          <div style={{fontSize:11,color:"var(--color-gray)",marginBottom:4}}>{formatTime(notes[0].created_at)}</div>
                          <div style={{fontSize:13,lineHeight:1.5}}>{notes[0].note}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {detailTab==="notes" && (
                  <div>
                    <div className="add-note-box">
                      <div className="note-type-tabs">
                        {NOTE_TYPES.map(t=>(
                          <button key={t.value} className={`note-type-btn ${noteType===t.value?"active":""}`} onClick={()=>setNoteType(t.value)}>
                            {t.label}
                          </button>
                        ))}
                      </div>
                      <textarea value={newNote} onChange={e=>setNewNote(e.target.value)}
                        placeholder={`Add a ${NOTE_TYPES.find(t=>t.value===noteType)?.label.toLowerCase()}...`}
                        rows={3} style={{resize:"vertical"}}
                        onKeyDown={e=>{if(e.key==="Enter"&&e.metaKey)addNote();}}/>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
                        <span style={{fontSize:11,color:"var(--color-dark-gray)"}}>⌘+Enter to save</span>
                        <button className="btn btn-primary btn-sm" onClick={addNote} disabled={savingNote||!newNote.trim()}>
                          {savingNote?"Saving...":"Save Note"}
                        </button>
                      </div>
                    </div>
                    <div className="notes-timeline">
                      {notes.length===0&&<div className="empty-state" style={{padding:"24px 0"}}><p>No notes yet.</p></div>}
                      {notes.map(note=>(
                        <div key={note.id} className="timeline-note">
                          <div className="timeline-icon"><NotePencil size={14}/></div>
                          <div className="timeline-content">
                            <div className="timeline-meta">
                              <span className="timeline-type">{NOTE_TYPES.find(t=>t.value===note.note_type)?.label}</span>
                              <span className="timeline-time">{formatTime(note.created_at)}</span>
                              <button className="btn-icon" style={{opacity:0.4,marginLeft:"auto"}} onClick={()=>deleteNote(note.id)}><Trash size={12}/></button>
                            </div>
                            <div className="timeline-text">{note.note}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detailTab==="calls" && (
                  <div>
                    {getPhone(selected)&&<button className="btn btn-primary btn-sm" style={{width:"100%",marginBottom:12}} onClick={()=>dialerState.dial?.(getPhone(selected),callerId)}><Phone size={13} weight="fill"/> Call {selected.first_name}</button>}
                    {calls.length===0&&<div className="empty-state" style={{padding:"24px 0"}}><p>No calls yet.</p></div>}
                    {calls.map(call=>(
                      <div key={call.id} className="history-item" style={{cursor:"default"}}>
                        <div className={`direction-icon ${call.direction}`}>
                          {call.direction==="inbound"?<ArrowDown size={13}/>:<ArrowUp size={13}/>}
                        </div>
                        <div className="history-info">
                          <div className="history-number">{call.direction==="inbound"?call.from_number:call.to_number}</div>
                          <div className="history-meta">{call.direction} · {call.status} · {formatTime(call.created_at)}</div>
                        </div>
                        <span style={{fontSize:11,color:"var(--color-gray)"}}>{call.duration>0?`${call.duration}s`:"—"}</span>
                      </div>
                    ))}
                  </div>
                )}

                {detailTab==="edit" && (
                  <EditLeadForm lead={selected} onSave={(fields)=>{updateLead(selected.id,fields);setDetailTab("overview");}}/>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      </div>

      {showAdd && (
        <div className="modal-overlay" onClick={()=>setShowAdd(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><h3>Add Lead</h3><button className="btn-icon" onClick={()=>setShowAdd(false)}><X size={16}/></button></div>
            <form onSubmit={handleAdd} className="modal-body">
              <div className="form-row">
                <div className="form-group"><label className="label">First Name *</label><input required value={form.first_name} onChange={e=>setForm(f=>({...f,first_name:e.target.value}))} placeholder="John"/></div>
                <div className="form-group"><label className="label">Last Name *</label><input required value={form.last_name} onChange={e=>setForm(f=>({...f,last_name:e.target.value}))} placeholder="Smith"/></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="label">Phone</label><input value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="+1 555 000 0000"/></div>
                <div className="form-group"><label className="label">Mobile</label><input value={form.mobile} onChange={e=>setForm(f=>({...f,mobile:e.target.value}))} placeholder="+1 555 000 0001"/></div>
              </div>
              <div className="form-group"><label className="label">Email</label><input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="john@company.com"/></div>
              <div className="form-row">
                <div className="form-group"><label className="label">Company</label><input value={form.company} onChange={e=>setForm(f=>({...f,company:e.target.value}))} placeholder="Acme Inc."/></div>
                <div className="form-group"><label className="label">Position</label><input value={form.position} onChange={e=>setForm(f=>({...f,position:e.target.value}))} placeholder="CEO"/></div>
              </div>
              <div className="form-group">
                <label className="label">Lead Source</label>
                <select value={form.lead_source} onChange={e=>setForm(f=>({...f,lead_source:e.target.value}))}>
                  <option value="">Select source</option>
                  {LEAD_SOURCES.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowAdd(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save Lead</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function EditLeadForm({ lead, onSave }) {
  const [form, setForm] = useState({
    first_name:lead.first_name||"", last_name:lead.last_name||"",
    phone:lead.phone||"", mobile:lead.mobile||"",
    email:lead.email||"", company:lead.company||"",
    position:lead.position||"", lead_source:lead.lead_source||"",
    lead_value:lead.lead_value||"",
    follow_up_date:lead.follow_up_date?lead.follow_up_date.split("T")[0]:"",
  });
  return (
    <form onSubmit={e=>{e.preventDefault();onSave(form);}} style={{padding:"0 4px"}}>
      <div className="form-row">
        <div className="form-group"><label className="label">First Name</label><input value={form.first_name} onChange={e=>setForm(f=>({...f,first_name:e.target.value}))}/></div>
        <div className="form-group"><label className="label">Last Name</label><input value={form.last_name} onChange={e=>setForm(f=>({...f,last_name:e.target.value}))}/></div>
      </div>
      <div className="form-row">
        <div className="form-group"><label className="label">Phone</label><input value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))}/></div>
        <div className="form-group"><label className="label">Mobile</label><input value={form.mobile} onChange={e=>setForm(f=>({...f,mobile:e.target.value}))}/></div>
      </div>
      <div className="form-group"><label className="label">Email</label><input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}/></div>
      <div className="form-row">
        <div className="form-group"><label className="label">Company</label><input value={form.company} onChange={e=>setForm(f=>({...f,company:e.target.value}))}/></div>
        <div className="form-group"><label className="label">Position</label><input value={form.position} onChange={e=>setForm(f=>({...f,position:e.target.value}))}/></div>
      </div>
      <div className="form-row">
        <div className="form-group"><label className="label">Lead Value ($)</label><input type="number" value={form.lead_value} onChange={e=>setForm(f=>({...f,lead_value:e.target.value}))} placeholder="0"/></div>
        <div className="form-group"><label className="label">Follow Up Date</label><input type="date" value={form.follow_up_date} onChange={e=>setForm(f=>({...f,follow_up_date:e.target.value}))}/></div>
      </div>
      <div className="form-group">
        <label className="label">Lead Source</label>
        <select value={form.lead_source} onChange={e=>setForm(f=>({...f,lead_source:e.target.value}))}>
          <option value="">Select source</option>
          {["Web","Phone","Email","Referral","LinkedIn","Cold Call","Event","Partner","Other"].map(s=><option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <button type="submit" className="btn btn-primary" style={{width:"100%",marginTop:16}}>Save Changes</button>
    </form>
  );
}
